import React, { useState, useRef, useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { getCtaShaderProps } from '../effects/shaderGradientPresets.js';
import { useIsMobile } from '../../../hooks/useIsMobile.js';
import { usePanelRevealObserver } from '../../../hooks/usePanelRevealObserver.js';
import { loadRecaptchaScript, getRecaptchaToken } from '../../../utils/recaptcha.js';
import { getPublicContactEmail } from '../../../utils/publicContactEmail.js';

import { CONTACT_CATEGORIES } from '../../../constants/contactCategories.js';

const CATEGORIES = CONTACT_CATEGORIES;

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  category: '',
  message: '',
  privacyAgreed: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\d\-+() ]*$/;

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.filter(c => c.value).map(c => [c.value, c.label]));

const ContactPage = () => {
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [serverError, setServerError] = useState('');
  const [submitResult, setSubmitResult] = useState(null);
  const formRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  usePanelRevealObserver('.hub-panel-animated', [submitted, confirming], '100px 0px -5% 0px');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};

    if (!form.name.trim()) {
      errs.name = 'お名前を入力してください。';
    } else if (form.name.trim().length > 100) {
      errs.name = 'お名前は100文字以内で入力してください。';
    }

    if (!form.email.trim()) {
      errs.email = 'メールアドレスを入力してください。';
    } else if (!EMAIL_RE.test(form.email.trim())) {
      errs.email = '有効なメールアドレスを入力してください。';
    }

    if (form.phone && !PHONE_RE.test(form.phone)) {
      errs.phone = '電話番号の形式が正しくありません。';
    }

    if (form.organization && form.organization.length > 200) {
      errs.organization = '200文字以内で入力してください。';
    }

    if (!form.category) {
      errs.category = 'お問い合わせ種別を選択してください。';
    }

    if (!form.message.trim()) {
      errs.message = 'お問い合わせ内容を入力してください。';
    } else if (form.message.trim().length < 10) {
      errs.message = '10文字以上で入力してください。';
    } else if (form.message.trim().length > 5000) {
      errs.message = '5000文字以内で入力してください。';
    }

    if (!form.privacyAgreed) {
      errs.privacyAgreed = 'プライバシーポリシーに同意してください。';
    }

    return errs;
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    setServerError('');

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErrorField = formRef.current?.querySelector('.contact-field-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setConfirming(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToEdit = () => {
    setConfirming(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setServerError('');

    console.group('[Contact] お問い合わせ送信');
    console.log('送信データ:', { name: form.name.trim(), email: form.email.trim(), category: form.category });

    try {
      const recaptchaToken = await getRecaptchaToken('contact');

      const response = await fetch(buildApiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          organization: form.organization.trim(),
          category: form.category,
          message: form.message.trim(),
          recaptchaToken,
        }),
      });

      console.log('レスポンスステータス:', response.status);
      const data = await response.json();
      console.log('レスポンスボディ:', data);

      if (!response.ok) {
        console.error('サーバーエラー:', data);
        if (response.status === 429) {
          setServerError('送信回数の制限に達しました。5分後にもう一度お試しください。');
          showToast('contact.error', { message: '送信回数の制限に達しました。' });
        } else {
          setServerError(data.message || 'お問い合わせの送信に失敗しました。');
          showToast('contact.error', {
            message: data.message || 'お問い合わせの送信に失敗しました。'
          });
        }
        return;
      }

      if (data.smtpConfigured === false) {
        console.warn('メール送信基盤が未設定のため、メールは送信されていません');
      } else {
        console.log('管理者通知メール:', data.emailSent ? '送信成功' : '送信失敗');
        console.log('自動返信メール:', data.autoReplySent ? '送信成功' : '送信失敗');
      }

      setSubmitResult(data);
      setSubmitted(true);
      showToast('contact.success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('ネットワークエラー:', err);
      setServerError('ネットワークエラーが発生しました。接続を確認してもう一度お試しください。');
      showToast('contact.error', { message: 'ネットワークエラーが発生しました。' });
    } finally {
      console.groupEnd();
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setServerError('');
    setSubmitted(false);
    setConfirming(false);
    setSubmitResult(null);
  };

  // ====================== Success view ======================
  if (submitted) {
    return (
      <div className="hub-contact-page">
        <div className="hub-fixed-gradient-container">
          <div className="hub-fixed-gradient-layer active">
            <ShaderGradientCanvas
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            >
              <Suspense fallback={null}>
                <ShaderGradient {...getCtaShaderProps(isMobile)} />
              </Suspense>
            </ShaderGradientCanvas>
          </div>
        </div>
        <section className="hub-contact-hero">
          <div className="hub-contact-hero-inner">
            <div className="hub-panel-animated hub-contact-success-panel">
              <div className="hub-contact-success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h1>お問い合わせありがとうございます</h1>
              <p className="hub-contact-success-message">
                お問い合わせ内容を受け付けました。<br />
                {submitResult?.autoReplySent ? (
                  <>ご入力いただいたメールアドレスに確認メールをお送りしましたのでご確認ください。<br /></>
                ) : (
                  <>確認メールの送信に失敗しました。メールが届かない場合はお手数ですが直接ご連絡ください。<br /></>
                )}
                担当者より <strong>2営業日以内</strong> にご連絡いたします。
              </p>
              <div className="hub-contact-success-actions">
                <Link to="/" className="hub-btn hub-btn-gradient hub-btn-lg">
                  トップページに戻る
                </Link>
                <button type="button" className="hub-btn hub-btn-outline" onClick={handleReset}>
                  別のお問い合わせをする
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ====================== Confirm view ======================
  if (confirming) {
    return (
      <div className="hub-contact-page">
        <div className="hub-fixed-gradient-container">
          <div className="hub-fixed-gradient-layer active">
            <ShaderGradientCanvas
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            >
              <Suspense fallback={null}>
                <ShaderGradient {...getCtaShaderProps(isMobile)} />
              </Suspense>
            </ShaderGradientCanvas>
          </div>
        </div>
        <section className="hub-contact-hero">
          <div className="hub-contact-hero-inner">
            <div className="hub-panel-animated hub-contact-confirm-panel">
              <h1>内容の確認</h1>
              <p className="hub-contact-confirm-subtitle">
                以下の内容でお問い合わせを送信します。内容をご確認ください。
              </p>

              {serverError && (
                <div className="hub-contact-server-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18, flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="9" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  <span>{serverError}</span>
                </div>
              )}

              <dl className="hub-contact-confirm-list">
                <div className="hub-contact-confirm-item">
                  <dt>お名前</dt>
                  <dd>{form.name}</dd>
                </div>
                <div className="hub-contact-confirm-item">
                  <dt>メールアドレス</dt>
                  <dd>{form.email}</dd>
                </div>
                {form.phone && (
                  <div className="hub-contact-confirm-item">
                    <dt>電話番号</dt>
                    <dd>{form.phone}</dd>
                  </div>
                )}
                {form.organization && (
                  <div className="hub-contact-confirm-item">
                    <dt>会社/クリニック名</dt>
                    <dd>{form.organization}</dd>
                  </div>
                )}
                <div className="hub-contact-confirm-item">
                  <dt>お問い合わせ種別</dt>
                  <dd>{CATEGORY_LABELS[form.category] || form.category}</dd>
                </div>
                <div className="hub-contact-confirm-item hub-contact-confirm-message">
                  <dt>お問い合わせ内容</dt>
                  <dd>{form.message}</dd>
                </div>
              </dl>

              <div className="hub-contact-confirm-actions">
                <button
                  type="button"
                  className="hub-btn hub-btn-outline"
                  onClick={handleBackToEdit}
                  disabled={submitting}
                >
                  修正する
                </button>
                <button
                  type="button"
                  className="hub-btn hub-btn-gradient hub-btn-lg"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="hub-contact-spinner" />
                      送信中...
                    </>
                  ) : (
                    '送信する'
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ====================== Form view ======================
  return (
    <div className="hub-contact-page">
      <div className="hub-fixed-gradient-container">
        <div className="hub-fixed-gradient-layer active">
          <ShaderGradientCanvas
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          >
            <Suspense fallback={null}>
              <ShaderGradient {...getCtaShaderProps(isMobile)} />
            </Suspense>
          </ShaderGradientCanvas>
        </div>
      </div>

      {/* Hero */}
      <section className="hub-contact-hero" ref={heroRef}>
        <div className="hub-contact-hero-inner">
          <div className="hub-panel-animated">
            <h1>お問い合わせ</h1>
            <p className="hub-contact-hero-subtitle">
              Synapseに関するご質問・ご相談がございましたら、<br />
              下記フォームよりお気軽にお問い合わせください。
            </p>

          </div>
        </div>
      </section>

      {/*
        Info cards（メール / 対応時間 / 回答目安）
        対応体制がまだ揃っていないため、現在は非表示。
        体制が整い次第、このコメントを外して表示を有効化すること。
      */}
      {/* <section className="hub-contact-info">
        <div className="hub-contact-info-inner">
          <div className="hub-panel-animated hub-contact-info-card">
            <div className="hub-contact-info-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h3>メール</h3>
            <a href={`mailto:${getPublicContactEmail()}`}>{getPublicContactEmail()}</a>
          </div>
          <div className="hub-panel-animated hub-contact-info-card">
            <div className="hub-contact-info-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h3>対応時間</h3>
            <p>平日 9:00 - 18:00<br /><span className="hub-contact-info-note">（土日祝日を除く）</span></p>
          </div>
          <div className="hub-panel-animated hub-contact-info-card">
            <div className="hub-contact-info-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3>回答目安</h3>
            <p>2営業日以内にご連絡</p>
          </div>
        </div>
      </section> */}

      {/* Form */}
      <section className="hub-contact-form-section">
        <div className="hub-contact-form-inner">
          <form
            ref={formRef}
            className="hub-panel-animated hub-contact-form"
            onSubmit={handleConfirm}
            noValidate
          >
            <h2 className="hub-contact-form-title">お問い合わせフォーム</h2>

            {serverError && (
              <div className="hub-contact-server-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18, flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="9" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span>{serverError}</span>
              </div>
            )}

            {/* Name */}
            <div className={`hub-contact-field ${errors.name ? 'contact-field-error' : ''}`}>
              <label htmlFor="contact-name">
                お名前 <span className="hub-contact-required">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="例: 田中 太郎"
                maxLength={100}
                autoComplete="name"
              />
              {errors.name && <p className="hub-contact-error">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className={`hub-contact-field ${errors.email ? 'contact-field-error' : ''}`}>
              <label htmlFor="contact-email">
                メールアドレス <span className="hub-contact-required">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="例: tanaka@example.com"
                autoComplete="email"
              />
              {errors.email && <p className="hub-contact-error">{errors.email}</p>}
            </div>

            {/* Two-column: Phone + Organization */}
            <div className="hub-contact-field-row">
              <div className={`hub-contact-field ${errors.phone ? 'contact-field-error' : ''}`}>
                <label htmlFor="contact-phone">電話番号</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="例: 03-1234-5678"
                  autoComplete="tel"
                />
                {errors.phone && <p className="hub-contact-error">{errors.phone}</p>}
              </div>
              <div className={`hub-contact-field ${errors.organization ? 'contact-field-error' : ''}`}>
                <label htmlFor="contact-organization">会社/クリニック名</label>
                <input
                  id="contact-organization"
                  name="organization"
                  type="text"
                  value={form.organization}
                  onChange={handleChange}
                  placeholder="例: サンプルクリニック"
                  maxLength={200}
                  autoComplete="organization"
                />
                {errors.organization && <p className="hub-contact-error">{errors.organization}</p>}
              </div>
            </div>

            {/* Category */}
            <div className={`hub-contact-field ${errors.category ? 'contact-field-error' : ''}`}>
              <label htmlFor="contact-category">
                お問い合わせ種別 <span className="hub-contact-required">*</span>
              </label>
              <select
                id="contact-category"
                name="category"
                value={form.category}
                onChange={handleChange}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value} disabled={!cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              {errors.category && <p className="hub-contact-error">{errors.category}</p>}
            </div>

            {/* Message */}
            <div className={`hub-contact-field ${errors.message ? 'contact-field-error' : ''}`}>
              <label htmlFor="contact-message">
                お問い合わせ内容 <span className="hub-contact-required">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="お問い合わせ内容を入力してください（10文字以上）"
                rows={6}
                maxLength={5000}
              />
              <div className="hub-contact-char-count">
                {form.message.length} / 5,000
              </div>
              {errors.message && <p className="hub-contact-error">{errors.message}</p>}
            </div>

            {/* Privacy */}
            <div className={`hub-contact-field hub-contact-checkbox-field ${errors.privacyAgreed ? 'contact-field-error' : ''}`}>
              <label className="hub-contact-checkbox-label">
                <input
                  type="checkbox"
                  name="privacyAgreed"
                  checked={form.privacyAgreed}
                  onChange={handleChange}
                />
                <span>
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</Link>に同意する
                </span>
              </label>
              {errors.privacyAgreed && <p className="hub-contact-error">{errors.privacyAgreed}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="hub-btn hub-btn-gradient hub-btn-lg hub-contact-submit"
            >
              確認画面へ
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
